import { useParams, Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import {
  ArrowLeft,
  Monitor,
  Clock,
  Globe,
  User,
} from "lucide-react";

export function AdminUserSessionPage() {
  const { userId } = useParams<{ userId: string }>();

  const {
    data: sessionData,
    isLoading,
    error,
  } = trpc.admin.getUserSessions.useQuery(
    { userId: userId! },
    {
      enabled: !!userId,
    }
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">Error loading sessions: {error.message}</div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">User not found</div>
      </div>
    );
  }

  const { user, sessions } = sessionData;

  const isSessionExpired = (expiresAt: string | Date) => {
    return new Date(expiresAt) < new Date();
  };

  const parseUserAgent = (userAgent: string | null) => {
    if (!userAgent) return "Unknown";
    // Simple parsing - could be enhanced with a user-agent parser library
    if (userAgent.includes("Chrome")) return "Chrome";
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Safari")) return "Safari";
    if (userAgent.includes("Edge")) return "Edge";
    return userAgent.substring(0, 50) + "...";
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/users/${userId}`}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to User
          </Link>
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Monitor className="h-6 w-6 text-violet-pulse" />
        <h1 className="text-2xl font-bold text-silica font-display">Sessions: {user.name || user.email}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            All Sessions ({sessions.length})
          </CardTitle>
          <CardDescription>
            Session history for {user.name || user.email}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-chromium-mist">
              <Monitor className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No sessions found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Browser</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Impersonated By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((sess: typeof sessions[number]) => (
                  <TableRow key={sess.id}>
                    <TableCell className="font-medium text-silica">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-chromium-mist" />
                        <span title={sess.userAgent || "Unknown"}>
                          {parseUserAgent(sess.userAgent)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-chromium-mist">
                      {sess.ipAddress || "—"}
                    </TableCell>
                    <TableCell className="text-chromium-mist">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {sess.createdAt
                          ? new Date(sess.createdAt).toLocaleString()
                          : "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-chromium-mist">
                      {sess.expiresAt
                        ? new Date(sess.expiresAt).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {isSessionExpired(sess.expiresAt) ? (
                        <Badge variant="secondary">Expired</Badge>
                      ) : (
                        <Badge className="bg-neon-green text-obsidian">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-chromium-mist">
                      {sess.impersonatedBy ? (
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-violet-pulse" />
                          <span className="font-mono text-xs">{sess.impersonatedBy.slice(0, 8)}...</span>
                        </div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
